import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "~/hooks/redux";
import Preloader from "~/components/Shared/Preloader";
import { getProfile } from "~/store/actions";
import Notary from "~/model/notary";

type ProfileContextValue = {
  notary: Notary | undefined;
  isLoadInProgress: boolean;
  isProfileSetupRequired: () => boolean;
  refreshProfile: () => void;
  isGlbaRequired: () => boolean;
};

const ProfileContext = React.createContext<ProfileContextValue | undefined>(undefined);

type TProfileProviderProps = {
  children: React.ReactNode;
};

const ProfileProvider = (props: TProfileProviderProps) => {
  const dispatch = useAppDispatch();

  const notary = useAppSelector(state => state.Profile.Single.notary);
  const isLoadInProgress = useAppSelector(state => state.Profile.Single.isLoadInProgress);
  const isAuth = useAppSelector(state => state.Auth.Login.isAuth);

  // returns TRUE if the notary profile is not yet completely set up
  // we use the 'distanceServed' field to check that because it is added in the last wizard step
  // so we can assume that if the user has reached that far, he has completed the entire profile
  // (note that this field accepts a value of 0 so we must check for NULL)
  // also we use the 'termsAcceptedTs' field to check that because imported notaries have all their fields completed
  // (including the 'distanceServed')
  // but they still need to agree to terms
  const isProfileSetupRequired = useCallback(
    (): boolean => !!(notary && (!notary.termsAcceptedTs || notary.distanceServed === null)),
    [notary]
  );

  const isGlbaRequired = useCallback(
    (): boolean => !!(notary && notary.forceGlbaComplete),
    [notary]
  );

  const refreshProfile = useCallback(() => {
    dispatch(getProfile());
  }, []);

  const value = useMemo(
    () => ({ notary: notary || undefined, isLoadInProgress, isProfileSetupRequired, refreshProfile, isGlbaRequired }),
    [notary, isLoadInProgress, isProfileSetupRequired, refreshProfile, isGlbaRequired]
  );

  useEffect(() => {
    // fetch subscription details from backend
    refreshProfile();
  }, [refreshProfile]);

  // wait until both auth (/me) and profile (/me/notary-profile) have resolved
  // both requests fire in parallel because AuthContext no longer blocks its children with a preloader
  if (isAuth !== null && notary !== undefined) {
    return <ProfileContext.Provider value={value} {...props} />;
  }
  // until then show a preloader
  return <Preloader />;
};

// helper hook that makes context data available
export const useProfile = (): ProfileContextValue => {
  const context = React.useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export default ProfileProvider;

